import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, User, LogOut } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useParentAuth } from "@/context/ParentAuthContext";

const links = [
  { to: "/", label: "Home", end: true },
  { to: "/competitions", label: "Competitions" },
  { to: "/summer-camp", label: "Summer Camp" },
  { to: "/sponsors", label: "Sponsors" },
  { to: "/contact", label: "Contact" },
];

function CubeMark() {
  return (
    <div className="flex items-center">
      <img src="/assets/logo.png" alt="Naptown Cubers" className="h-12 md:h-14 w-auto" />
    </div>
  );
}

export default function PublicLayout() {
  const [open, setOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const { parent, loading, logout } = useParentAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const signinPath = `/signin?next=${encodeURIComponent(location.pathname + location.search)}`;
  useEffect(() => { setOpen(false); }, [location.pathname]);
  const onLogout = async () => {
    setLoggingOut(true);
    if (location.pathname.startsWith("/parent")) navigate("/", { replace: true });
    try { await logout(); setOpen(false); toast.success("Logged out"); }
    catch { toast.error("Could not log out. Please try again."); }
    finally { setLoggingOut(false); }
  };
  return (
    <div className="min-h-screen bg-[#FDFFFC] text-[#1E1E24]">
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-100/60">
        <div className="max-w-7xl mx-auto px-5 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="brand-link"><CubeMark /></Link>
          <nav className="hidden lg:flex items-center gap-1" aria-label="Main navigation">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-full text-sm font-semibold transition-colors ${
                    isActive
                      ? "bg-[#FF9F1C] text-white shadow-[0_8px_20px_rgb(255,159,28,0.28)]"
                      : "text-[#1E1E24] hover:bg-[#FFBF69]/20"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            {!loading && (parent ? <>
              <NavLink to="/parent" data-testid="nav-parent-portal" className={({ isActive }) => `ml-2 px-3 py-2 rounded-full text-sm font-semibold ${isActive ? "bg-[#FF9F1C] text-white" : "bg-[#1E1E24] text-white hover:bg-[#B36906]"}`}>Parent Portal</NavLink>
              <span title={parent.email} className="hidden xl:inline-flex items-center gap-1 ml-2 text-xs text-[#7A7D8C] max-w-24 truncate" data-testid="nav-parent-identity"><User className="w-3.5 h-3.5 shrink-0" />{parent.name?.split(" ")[0] || "Parent"}</span>
              <button onClick={onLogout} disabled={loggingOut} data-testid="nav-logout" className="ml-2 px-3 py-2 rounded-full text-sm font-semibold hover:bg-[#FFBF69]/20 disabled:opacity-60 inline-flex items-center gap-1"><LogOut className="w-3.5 h-3.5" />{loggingOut ? "Logging out…" : "Log Out"}</button>
            </> : <Link to={signinPath} data-testid="nav-signin" className="ml-3 px-4 py-2 rounded-full text-sm font-semibold bg-[#1E1E24] text-white hover:bg-[#FF9F1C] transition-colors inline-flex items-center gap-2"><User className="w-3.5 h-3.5" />Sign In</Link>)}
          </nav>
          <button className="lg:hidden p-2 rounded-full bg-[#FFBF69]/20" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden bg-white border-t border-slate-100 px-5 pb-6 flex flex-col gap-2">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setOpen(false)} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => `px-4 py-3 rounded-2xl text-sm font-semibold ${isActive ? "bg-[#FF9F1C] text-white" : "hover:bg-[#FFBF69]/20"}`}>
                {l.label}
              </NavLink>
            ))}
            {!loading && (parent ? <>
              <span className="px-4 py-2 text-xs text-[#7A7D8C]" data-testid="mobile-parent-identity">Signed in as {parent.name || parent.email}</span>
              <NavLink to="/parent" onClick={() => setOpen(false)} data-testid="mobile-parent-portal" className="px-4 py-3 rounded-2xl text-sm font-semibold bg-[#1E1E24] text-white">Parent Portal</NavLink>
              <button onClick={onLogout} disabled={loggingOut} data-testid="mobile-logout" className="px-4 py-3 text-left rounded-2xl text-sm font-semibold border border-slate-100 disabled:opacity-60">{loggingOut ? "Logging out…" : "Log Out"}</button>
            </> : <NavLink to={signinPath} onClick={() => setOpen(false)} data-testid="mobile-signin" className="px-4 py-3 rounded-2xl text-sm font-semibold border border-slate-100">Sign In</NavLink>)}
          </div>
        )}
      </header>

      <main>
        <Outlet />
      </main>

      <footer className="mt-24 border-t border-slate-100 bg-white/60">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-14 grid gap-10 md:grid-cols-3">
          <div>
            <CubeMark />
            <p className="mt-4 text-sm text-[#7A7D8C] leading-relaxed max-w-sm">
              A friendly Annapolis-area community for young cubers. We run competitions, host summer camps, and cheer for every personal best.
            </p>
          </div>
          <div>
            <h4 className="font-display font-bold mb-3">Explore</h4>
            <ul className="space-y-2 text-sm">
              {links.map((l) => (<li key={l.to}><Link to={l.to} className="text-[#7A7D8C] hover:text-[#FF9F1C]">{l.label}</Link></li>))}
            </ul>
          </div>
          <div>
            <h4 className="font-display font-bold mb-3">Say hi</h4>
            <p className="text-sm text-[#7A7D8C]"><a href="mailto:naptowncubers@gmail.com" className="hover:text-[#FF9F1C]">naptowncubers@gmail.com</a></p>
            <p className="text-sm text-[#7A7D8C]">Annapolis, MD</p>
            {!loading && <Link to={parent ? "/parent" : "/signin"} data-testid="footer-account-link" className="mt-4 inline-block text-xs uppercase tracking-wider font-semibold text-[#7A7D8C] hover:text-[#FF9F1C]">{parent ? "Parent Portal" : "Sign In"} →</Link>}
          </div>
        </div>
        <div className="border-t border-slate-100 py-5 text-center text-xs text-[#7A7D8C]">
          © {new Date().getFullYear()} Naptown Cubers. Turn, smile, repeat.
        </div>
      </footer>
    </div>
  );
}
